import type { GitpodAPI } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { type Prompt } from "gitpod-next-api/gitpod/v1/agent_pb";
import { type PlainMessage, toPlainMessage } from "@bufbuild/protobuf";
import { DEFAULT_PAGE_SIZE } from "@/queries/pagination";
import { PaginationRequest } from "gitpod-next-api/gitpod/v1/pagination_pb";
import { ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { useEventInterest } from "@/hooks/use-event-interest";

export type PlainPrompt = PlainMessage<Prompt>;

const toPlainPrompt = (prompt: Prompt): PlainPrompt => {
    return toPlainMessage(prompt);
};

export const promptServiceQueryKey = {
    listPrompts: (filter?: { isCommand?: boolean; isSkill?: boolean; isTemplate?: boolean }) =>
        keyWithPrincipal(["prompts", "list", filter]),
    listPromptsPaginated: (token: string, filter?: { isCommand?: boolean; isSkill?: boolean; isTemplate?: boolean; search?: string }) =>
        keyWithPrincipal(["prompts", "list-paginated", token, filter]),
    getPrompt: (promptId?: string) => keyWithPrincipal(["prompts", "get", promptId]),
};

export const useListPromptsOptions = (filter?: {
    isCommand?: boolean;
    isSkill?: boolean;
    isTemplate?: boolean;
    excludePromptContent?: boolean;
}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: promptServiceQueryKey.listPrompts(filter),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const response = await api.agentService.listPrompts({
                pagination: new PaginationRequest({ pageSize: DEFAULT_PAGE_SIZE }),
                filter: filter
                    ? {
                          isCommand: filter.isCommand,
                          isSkill: filter.isSkill,
                          isTemplate: filter.isTemplate,
                          excludePromptContent: filter.excludePromptContent,
                      }
                    : undefined,
            });
            return {
                prompts: response.prompts.map(toPlainPrompt),
            };
        },
        enabled: !!user,
    });
};

export const useListPrompts = (filter?: {
    isCommand?: boolean;
    isSkill?: boolean;
    isTemplate?: boolean;
    excludePromptContent?: boolean;
}) => {
    useEventInterest({ resourceType: ResourceType.PROMPT });
    return useQuery(useListPromptsOptions(filter));
};

export type PromptsCursorPageData = {
    prompts: PlainPrompt[];
    nextToken: string;
};

/**
 * Returns queryOptions for fetching a single page of prompts using an opaque cursor token.
 * Pair with useCursorPagination() for prev/next navigation.
 */
export const usePromptsCursorPageOptions = ({
    token = "",
    search = "",
    filter,
}: {
    token?: string;
    search?: string;
    filter?: {
        isCommand?: boolean;
        isSkill?: boolean;
        isTemplate?: boolean;
        excludePromptContent?: boolean;
    };
} = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: promptServiceQueryKey.listPromptsPaginated(token, { ...filter, search }),
        queryFn: async (): Promise<PromptsCursorPageData> => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const response = await api.agentService.listPrompts({
                pagination: new PaginationRequest({ pageSize: DEFAULT_PAGE_SIZE, token }),
                filter: {
                    isCommand: filter?.isCommand,
                    isSkill: filter?.isSkill,
                    isTemplate: filter?.isTemplate,
                    excludePromptContent: filter?.excludePromptContent,
                    search,
                },
            });
            return {
                prompts: response.prompts.map(toPlainPrompt),
                nextToken: response.pagination?.nextToken ?? "",
            };
        },
        placeholderData: keepPreviousData,
        enabled: !!user,
    });
};

export const usePromptsCursorPage = ({
    token = "",
    search = "",
    filter,
}: {
    token?: string;
    search?: string;
    filter?: {
        isCommand?: boolean;
        isSkill?: boolean;
        isTemplate?: boolean;
        excludePromptContent?: boolean;
    };
} = {}) => {
    useEventInterest({ resourceType: ResourceType.PROMPT });
    return useQuery(usePromptsCursorPageOptions({ token, search, filter }));
};

export const useGetPromptOptions = (promptId?: string) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: promptServiceQueryKey.getPrompt(promptId),
        queryFn: async () => {
            if (!promptId) {
                throw new Error("Prompt ID is required");
            }
            const response = await api.agentService.getPrompt({ promptId });
            if (!response.prompt) {
                throw new Error("Prompt not found");
            }
            return toPlainPrompt(response.prompt);
        },
        enabled: !!promptId,
    });
};

export const useGetPrompt = (promptId?: string) => {
    return useQuery(useGetPromptOptions(promptId));
};

export const useCreatePrompt = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: {
            name: string;
            description: string;
            prompt: string;
            isTemplate?: boolean;
            isCommand?: boolean;
            isSkill?: boolean;
            command?: string;
        }) => {
            const response = await api.agentService.createPrompt(params);
            return response.prompt ? toPlainPrompt(response.prompt) : null;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: promptServiceQueryKey.listPrompts().slice(0, -1),
            });
        },
    });
};

export const useUpdatePrompt = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: {
            promptId: string;
            metadata?: {
                name?: string;
                description?: string;
            };
            spec?: {
                prompt?: string;
                isTemplate?: boolean;
                isCommand?: boolean;
                isSkill?: boolean;
                command?: string;
            };
        }) => {
            const response = await api.agentService.updatePrompt(params);
            return response.prompt ? toPlainPrompt(response.prompt) : null;
        },
        onSuccess: (_data, variables) => {
            void queryClient.invalidateQueries({
                queryKey: promptServiceQueryKey.listPrompts().slice(0, -1),
            });
            void queryClient.invalidateQueries({ queryKey: promptServiceQueryKey.getPrompt(variables.promptId) });
        },
    });
};

export const useDeletePrompt = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ promptId }: { promptId: string }) => {
            await api.agentService.deletePrompt({ promptId });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: promptServiceQueryKey.listPrompts().slice(0, -1),
            });
        },
    });
};

export const handlePromptEvent = (_api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    // Invalidate all listPrompts queries regardless of filter
    // This ensures commands, templates, and all prompts are refreshed
    void client.invalidateQueries({
        queryKey: promptServiceQueryKey.listPrompts().slice(0, -1),
    });

    if (evt.resourceId) {
        void client.invalidateQueries({ queryKey: promptServiceQueryKey.getPrompt(evt.resourceId) });
    }
};
